import { PRIMARY_OUTLET, UrlTree } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { AppRoutesDefinitions } from '@app/app.routes';
import { UserRole } from '@shared/models/role';
import {
  ACCOUNT_SWITCH_EMAIL_MASK,
  ACCOUNT_SWITCH_FALLBACK_NEXT,
  ACCOUNT_SWITCH_NEXT_PARAM,
  ACCOUNT_SWITCH_REASON_PARAM,
} from './account-switch.constants';
import { AccountSwitchReason } from './account-switch.enums';
import {
  AuthRedirectRoute,
  SessionAccount,
  SessionTokenClaims,
} from './account-switch.types';

export const readSessionAccount = (
  token: string | null,
): SessionAccount | null => {
  if (!token) {
    return null;
  }

  try {
    const { id, firstName, lastName, email, role, clientId, avatarLink } =
      jwtDecode<SessionAccount>(token);

    return id
      ? {
          id,
          firstName: firstName ?? null,
          lastName: lastName ?? null,
          email: email ?? null,
          role,
          clientId: clientId ?? null,
          avatarLink: avatarLink ?? null,
        }
      : null;
  } catch {
    return null;
  }
};

export const isSessionAlive = (
  refreshToken: string | null,
  now: number = Date.now(),
): boolean => {
  if (!refreshToken) {
    return false;
  }

  try {
    const { exp } = jwtDecode<SessionTokenClaims>(refreshToken);

    return Boolean(exp) && exp * 1000 > now;
  } catch {
    return false;
  }
};

export const toSafeNext = (value: string | null): string =>
  value?.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
    ? value
    : ACCOUNT_SWITCH_FALLBACK_NEXT;

export const toAccountSwitchReason = (
  value: string | null,
): AccountSwitchReason | null =>
  Object.values(AccountSwitchReason).find((reason) => reason === value) ?? null;

export const buildAccountSwitchUrl = (
  reason: AccountSwitchReason,
  next: string,
): string => {
  const query = new URLSearchParams({
    [ACCOUNT_SWITCH_REASON_PARAM]: reason,
    [ACCOUNT_SWITCH_NEXT_PARAM]: next,
  });

  return `/${AppRoutesDefinitions.ACCOUNT_SWITCH}?${query.toString()}`;
};

export const toAuthRedirectRoute = (tree: UrlTree): AuthRedirectRoute => ({
  url:
    tree.root.children[PRIMARY_OUTLET]?.segments
      .map((segment) => segment.path)
      .join('/') ?? '',
  params: tree.queryParams,
});

export const formatAccountName = ({
  firstName,
  lastName,
}: Pick<SessionAccount, 'firstName' | 'lastName'>): string =>
  [firstName, lastName ? `${lastName.charAt(0)}.` : null]
    .filter(Boolean)
    .join(' ');

export const maskEmail = (email: string | null | undefined): string | null => {
  const at = email?.indexOf('@') ?? -1;

  return at > 0
    ? `${email[0]}${ACCOUNT_SWITCH_EMAIL_MASK}${email.slice(at)}`
    : null;
};

export const toRoleKey = (role: UserRole): Lowercase<UserRole> =>
  role.toLowerCase() as Lowercase<UserRole>;
